import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  AddCommentFormData,
  addCommentSchema,
} from "@/features/recordings/types/add-comment-schema";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicRecordingComment } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { fetchRecordingCommentsKey } from "../api/fetch-recording-comments";
import { useUpdateRecordingComment } from "../api/update-recording-comment";

type EditCommentProps = {
  comment: PublicRecordingComment;
  setIsEditing: (isEditing: boolean) => void;
};

export function EditComment({ comment, setIsEditing }: EditCommentProps) {
  const { id: recordingId } = useParams();
  const { organisation } = useOrganisation();
  const form = useForm<AddCommentFormData>({
    resolver: zodResolver(addCommentSchema),
    defaultValues: {
      content: comment.content,
    },
  });

  const { mutateAsync: updateComment } = useUpdateRecordingComment(
    {
      orgId: organisation.id,
      recordingId: recordingId || "",
      commentId: comment.id,
    },
    {
      onSuccess: (comment) => {
        queryClient.setQueryData(
          fetchRecordingCommentsKey({
            organisationId: organisation.id,
            recordingId: recordingId || "",
          }),
          (old: PublicRecordingComment[]) =>
            old.map((c) => (c.id === comment.id ? comment : c)),
        );
      },
    },
  );

  const handleUpdateComment = async (data: AddCommentFormData) => {
    try {
      await updateComment({
        content: data.content,
      });

      setIsEditing(false);
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  };

  return (
    <Form {...form}>
      <form
        className="flex w-full flex-col gap-2 py-2"
        onSubmit={form.handleSubmit(handleUpdateComment)}
      >
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem className="flex flex-1 flex-col">
              <Input
                autoFocus
                placeholder="Add your comment.."
                {...field}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setIsEditing(false);
                  }
                }}
              />
              <FormMessage />
            </FormItem>
          )}
        />

        {form.formState.errors.root && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsEditing(false)}
          >
            Cancel
          </Button>
          <Button
            isLoading={form.formState.isSubmitting}
            isAnimated={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting || !form.formState.isValid}
          >
            Update
          </Button>
        </div>
      </form>
    </Form>
  );
}
